// Brief 69.1 (24 Jun 2026): the AI Once-Over (gap finder) logic lived inside
// CCLAIReview.jsx, rendered in a panel below the document column where Vanessa
// couldn't see its trigger. That component is gone; this hook is now the single
// source of truth for the sweep. CclEditor owns the `comments` state and the
// rail renders both the trigger and the results.
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../config/api";
import { errorMessage } from "../utils/errorMessage";

// Backend gap severities map onto the rail's comment "kind" badges.
const SEVERITY_KIND = { high: "warning", medium: "warning", low: "missing-info" };

// Normalise the backend gap shape ({ label, reason, severity }) into the rail's
// comment model. Gaps start advisory (suggestedText null) — a section is drafted
// on demand via draftSection when the fee earner clicks "Draft fix".
//
// BM-H17 (2 Jul 2026) — accepts an optional gapStatus map ({ [gapId]:
// { status } }) from the persisted ccl_reviews record so Accept / Reject
// decisions survive a page reload. Before BM-H17 the rail always came
// back with every card at status="open" because the state lived only
// in React.
// BM-H3747 (19 Oct 2026): a reply kept on the review (gap_replies) puts the
// redraft and the reply back on its card after a reload. A gap the once-over
// raised again from an earlier reply carries from_reply (and, when it has no
// exact place, reply_wording).
function replyOverlay(kept) {
  if (!kept) return {};
  const op = kept.op || "advisory";
  return {
    reply: { feedback: kept.feedback || "", at: kept.at || null, kept: true, op, note: kept.note || "" },
    suggestedText: op === "advisory" || op === "none" ? null : kept.wording || null,
    replyWording: op === "advisory" ? kept.wording || null : null,
    // "none": the reply settled the point with no change to the letter.
    op: op === "none" ? "advisory" : op,
    blockId: kept.block_id ?? null,
    anchorName: kept.anchor_name || null,
    targetText: kept.target_text || null,
    blockSha: kept.block_sha || null,
    blockText: kept.block_text || null,
    quote: kept.target_text || kept.anchor_quote || null,
    advisoryReason: kept.advisory_reason || null,
  };
}

function gapsToComments(gaps, ts = Date.now(), gapStatus = {}, gapReplies = {}) {
  return (gaps || []).map((gap, i) => {
    const id = `gap-${i}`;
    const persisted = gapStatus?.[id]?.status;
    const card = {
      id,
      anchorId: null,
      kind: SEVERITY_KIND[gap.severity] || "missing-info",
      title: gap.label || "Missing detail",
      body: gap.reason || "",
      // Verbatim excerpt of existing CCL text the AI identified as sitting
      // next to this gap (Brief: "AI improvements added to end instead of
      // correct paragraph", 28 Jul 2026). Used by handleInsertDraft to place
      // the accepted fix in-place instead of always appending at the tail.
      quote: gap.target_text || gap.anchor_quote || null,
      // BM-H596 (23 Sep 2026): where the fix goes, validated by the server
      // against the letter as it was checked. op "advisory" means it has no
      // exact place: the card shows the reason only and nothing is inserted.
      op: gap.op || "advisory",
      blockId: gap.block_id ?? null,
      anchorName: gap.anchor_name || null,
      targetText: gap.target_text || null,
      blockSha: gap.block_sha || null,
      blockText: gap.block_text || null,
      // BM-H3747: a "remove" fix takes out blockId to endBlockId.
      endBlockId: gap.end_block_id ?? null,
      endBlockText: gap.end_block_text || null,
      removeCount: gap.remove_count || null,
      advisoryReason: gap.advisory_reason || null,
      suggestedText: gap.op && gap.op !== "advisory" && gap.replacement ? gap.replacement : null,
      drafting: false,
      severity: gap.severity || "medium",
      gapLabel: gap.label || "",
      gapReason: gap.reason || "",
      timestamp: ts,
      status: persisted === "accepted" || persisted === "rejected" ? persisted : "open",
      fromReply: gap.from_reply || null,
      replyWording: gap.reply_wording || null,
      reply: null,
    };
    return { ...card, ...replyOverlay(gapReplies?.[id]) };
  });
}

// What the last once-over did with earlier replies, for the rail to say.
function replyNotice(reviewData) {
  const settled = reviewData?.settled_by_reply || [];
  if (!settled.length) return "";
  const n = settled.length;
  return `${n} point${n > 1 ? "s" : ""} you settled by reply ${n > 1 ? "were" : "was"} not raised again.`;
}

// BM-H596: the place a fix goes travels with every draft and refine call, and
// the server re-validates it against the letter as it is now.
function targetFields(comment) {
  return {
    anchor_quote: comment.op ? null : comment.quote || null,
    op: comment.op || null,
    block_id: comment.blockId ?? null,
    anchor_name: comment.anchorName || null,
    target_text: comment.targetText || null,
  };
}

function draftResult(data) {
  return {
    text: data.drafted_text || "",
    op: data.op || "advisory",
    blockId: data.block_id ?? null,
    anchorName: data.anchor_name || null,
    targetText: data.target_text || null,
    blockSha: data.block_sha || null,
    blockText: data.block_text || null,
    anchorQuote: data.anchor_quote ?? null,
    advisoryReason: data.advisory_reason || null,
    reply: data.reply || null,
    note: data.note || "",
  };
}

export { gapsToComments, replyNotice };

// BM-H3747: the server marks a run failed after ten minutes; wait a little longer.
const ONCE_OVER_MAX_WAIT_MS = 11 * 60 * 1000;

export function useCclSweep({
  submissionId,
  getCclText,
  matterType,
  feeQuoted,
  clientName,
  submissionData,
  setComments,
  // BM-H3747: how often to ask whether a background once-over has finished.
  pollMs = 4000,
}) {
  const [sweeping, setSweeping] = useState(false);
  const [notice, setNotice] = useState("");

  // The last gap analysis as rail cards, or null when there is none.
  const fetchLastReview = useCallback(async () => {
    const userId = sessionStorage.getItem("bonsai_user_id");
    const resp = await axios.get(`${API}/hub/ccl-review/${submissionId}`, {
      headers: { "X-Bonsai-User-Id": userId },
    });
    const reviews = resp.data.reviews || [];
    const last = reviews.find((r) => r.review_type === "gap_analysis");
    if (!last?.review_data?.gaps) return null;
    setNotice(replyNotice(last.review_data));
    return gapsToComments(last.review_data.gaps, Date.now(), last.gap_status || {}, last.gap_replies || {});
  }, [submissionId]);

  // Reload the last gap analysis on mount so re-opening the editor restores
  // prior results instead of an empty rail.
  useEffect(() => {
    if (!submissionId) return;
    let cancelled = false;
    (async () => {
      try {
        const cards = await fetchLastReview();
        if (!cancelled && cards) setComments(cards);
      } catch {}
    })();
    return () => {
      cancelled = true;
    };
  }, [submissionId, setComments, fetchLastReview]);

  const runSweep = useCallback(async () => {
    const cclText = getCclText();
    if (!cclText || cclText.length < 50) {
      toast.error("CCL is too short to analyse");
      return;
    }
    setSweeping(true);
    try {
      const userId = sessionStorage.getItem("bonsai_user_id");
      const resp = await axios.post(
        `${API}/hub/ccl-review`,
        {
          submission_id: submissionId,
          ccl_text: cclText,
          matter_type: matterType || "",
          fee_quoted: feeQuoted || "",
          client_name: clientName || "",
          submission_data: submissionData || {},
          review_mode: "gap_analysis",
        },
        { headers: { "X-Bonsai-User-Id": userId } }
      );
      let cards;
      if (resp.data.review) {
        cards = gapsToComments(resp.data.review.gaps || []);
      } else {
        // BM-H3747 (6 Oct 2026): the server runs the review in the background
        // so Azure's ~230 second limit cannot cut it off ("Backend call
        // failure"). Ask until it is done, then load the saved review.
        const deadline = Date.now() + ONCE_OVER_MAX_WAIT_MS;
        let status = resp.data;
        while (status.status === "running" && Date.now() < deadline) {
          await new Promise((r) => setTimeout(r, pollMs));
          const st = await axios.get(`${API}/hub/ccl-review/${submissionId}/run-status`, {
            headers: { "X-Bonsai-User-Id": userId },
          });
          status = st.data || {};
        }
        if (status.status === "running") {
          toast.error("The AI Once-Over is still running. Reload the page in a few minutes to see it.");
          return;
        }
        if (status.status !== "done") {
          toast.error(`The AI Once-Over did not finish (${status.reason || "no reason recorded"}). Please run it again.`);
          return;
        }
        cards = (await fetchLastReview()) || [];
      }
      const gaps = cards;
      setComments(cards);
      if (gaps.length === 0) {
        toast.success("No gaps found — CCL looks complete!");
      } else {
        toast.success(`Found ${gaps.length} suggestion${gaps.length > 1 ? "s" : ""} to review`);
      }
    } catch (err) {
      toast.error(errorMessage(err, "AI Once-Over failed"));
    } finally {
      setSweeping(false);
    }
  }, [submissionId, getCclText, matterType, feeQuoted, clientName, submissionData, setComments, pollMs, fetchLastReview]);

  // Draft replacement copy for a single gap. Returns the drafted text; the
  // caller is responsible for stashing it on the comment so Accept can insert it.
  const draftSection = useCallback(
    async (comment) => {
      const userId = sessionStorage.getItem("bonsai_user_id");
      const resp = await axios.post(
        `${API}/hub/ccl-draft-section`,
        {
          submission_id: submissionId,
          gap_label: comment.gapLabel,
          gap_reason: comment.gapReason,
          matter_type: matterType || "",
          fee_quoted: feeQuoted || "",
          client_name: clientName || "",
          submission_data: submissionData || {},
          ccl_text: getCclText(),
          ...targetFields(comment),
        },
        { headers: { "X-Bonsai-User-Id": userId } }
      );
      return draftResult(resp.data);
    },
    [submissionId, getCclText, matterType, feeQuoted, clientName, submissionData]
  );

  // BM-H121 (28 Jul 2026): re-draft a suggestion using the fee earner's own
  // reply text as feedback. Previously the Reply box in the rail was a
  // Phase-2 stub — it accepted the text, toasted "noted", and threw it
  // away. This is the real call.
  const refineSection = useCallback(
    async (comment, feedback) => {
      const userId = sessionStorage.getItem("bonsai_user_id");
      const resp = await axios.post(
        `${API}/hub/ccl-refine-section`,
        {
          submission_id: submissionId,
          gap_label: comment.gapLabel,
          gap_reason: comment.gapReason,
          previous_draft: comment.suggestedText || comment.replyWording || "",
          feedback,
          gap_id: comment.id,
          matter_type: matterType || "",
          fee_quoted: feeQuoted || "",
          client_name: clientName || "",
          submission_data: submissionData || {},
          ccl_text: getCclText(),
          ...targetFields(comment),
        },
        { headers: { "X-Bonsai-User-Id": userId } }
      );
      return draftResult(resp.data);
    },
    [submissionId, getCclText, matterType, feeQuoted, clientName, submissionData]
  );

  return { runSweep, sweeping, draftSection, refineSection, fetchLastReview, notice };
}
